import { useMemo, useState } from 'react';
import { Box, TextField, Button, Typography, CircularProgress } from '@mui/material';
import Modal from 'shared/Modal';
import { ModalPropsToOverride } from 'components/DynamicModalProvider/types';
import { useUpdateSyncFrequencyMutation } from 'IntegrationModule/api/queries/useUpdateSyncFrequencyMutation';
import {
  MIN_SYNC_FREQUENCY,
  MAX_SYNC_FREQUENCY,
  DEFAULT_SYNC_FREQUENCY,
  MAX_SCHEDULED_SYNC_TIMES,
  SYNC_FREQUENCY_APPROVAL_NOTE,
  formatUtcSyncTimeAsLocal,
  isValidSyncFrequency,
  isValidSyncTime,
  normalizeSyncTimes,
  requiresSyncFrequencyApproval,
} from 'IntegrationModule/utils/syncFrequency';

interface ChangeSyncFrequencyModalProps extends ModalPropsToOverride {
  mosaicTeamId: number;
  /** Target service slug, e.g. "deltek-ajera-cloud" (a.k.a. targetService). */
  serviceName: string;
  targetServiceId: number;
  /** Current explicit count; undefined means the integration uses the IS default. */
  currentDailyFetchCount?: number;
  /** Current scheduled UTC sync times; the IS stores a string (legacy) or array. */
  currentDailyFetchTime?: string | string[];
  tenant?: string;
  realmId?: string;
  /**
   * Called after a successful update so a redux-backed caller can refetch the
   * integration (the mutation only invalidates the react-query cache).
   */
  onUpdated?: () => void;
}

/**
 * Guard against a count stored malformed in the settings JSONB (numeric string,
 * fraction, zero/negative): only a positive integer is a usable daily count.
 * Anything else can't have come from this modal, so it degrades to the
 * no-explicit-count path instead of rendering a broken "2.5x per day" UI with
 * hidden time fields.
 */
const resolveExplicitCount = (currentDailyFetchCount?: number): number | null => {
  const rawStoredCount = currentDailyFetchCount != null ? Number(currentDailyFetchCount) : null;

  return rawStoredCount != null &&
    Number.isInteger(rawStoredCount) &&
    rawStoredCount >= MIN_SYNC_FREQUENCY
    ? rawStoredCount
    : null;
};

/**
 * The schedule the IS actually runs today. Anchors always win on the IS, so an
 * explicit count lower than the anchor count still runs one sync per anchor;
 * baseline on the anchors in that case or a pristine Save would truncate them. A
 * times-only integration (the common shape — anchors set, count unset) syncs
 * exactly once per anchor (IS: targetCount = count || anchors.length || 2); with
 * neither setting the IS default applies.
 */
const resolveEffectiveCurrent = (explicitCount: number | null, storedTimeCount: number): number => {
  if (explicitCount != null) return Math.max(explicitCount, storedTimeCount);

  return storedTimeCount > 0 ? storedTimeCount : DEFAULT_SYNC_FREQUENCY;
};

type SyncTimeValidity = {
  hasInvalidTime: boolean;
  hasDuplicateTimes: boolean;
  removesAllStoredTimes: boolean;
};

const resolveTimesError = ({
  hasInvalidTime,
  hasDuplicateTimes,
  removesAllStoredTimes,
}: SyncTimeValidity): string | null => {
  if (hasInvalidTime) return 'Sync times must be valid 24-hour HH:mm values.';
  if (hasDuplicateTimes) return 'Sync times must be unique.';
  if (removesAllStoredTimes) {
    return "This integration already has scheduled sync times — keep at least one. Fully removing them isn't supported here.";
  }

  return null;
};

/**
 * Label the schedule the IS actually runs, calling out when stored anchors
 * override a lower stored count ("anchors win").
 */
const resolveCurrentLabel = (
  explicitCount: number | null,
  storedTimeCount: number,
  effectiveCurrent: number,
): string => {
  if (explicitCount != null) {
    return storedTimeCount > explicitCount
      ? `${effectiveCurrent}x per day (scheduled times override the stored count of ${explicitCount})`
      : `${explicitCount}x per day`;
  }

  return storedTimeCount > 0
    ? `${storedTimeCount}x per day (from scheduled times)`
    : `Default (${DEFAULT_SYNC_FREQUENCY}x per day)`;
};

const ChangeSyncFrequencyModal = ({
  isOpen,
  toggle,
  mosaicTeamId,
  serviceName,
  targetServiceId,
  currentDailyFetchCount,
  currentDailyFetchTime,
  tenant,
  realmId,
  onUpdated,
}: ChangeSyncFrequencyModalProps) => {
  const { updateSyncFrequency, isUpdatingSyncFrequency } = useUpdateSyncFrequencyMutation();

  const storedTimes = useMemo(
    () => normalizeSyncTimes(currentDailyFetchTime),
    [currentDailyFetchTime],
  );

  const explicitCount = resolveExplicitCount(currentDailyFetchCount);

  // Baseline the inputs on what the IS actually runs so the pristine modal is a
  // guaranteed no-op.
  const effectiveCurrent = resolveEffectiveCurrent(explicitCount, storedTimes.length);

  const [inputValue, setInputValue] = useState(String(effectiveCurrent));
  // Fixed-size slots (one per anchor the IS honors) so lowering the count and
  // raising it back never loses what the admin typed; only the first
  // min(count, MAX) slots are shown and submitted.
  const [timeInputs, setTimeInputs] = useState<string[]>(() =>
    Array.from({ length: MAX_SCHEDULED_SYNC_TIMES }, (_, index) => storedTimes[index] ?? ''),
  );

  const parsed = Number(inputValue);
  const isValid = inputValue.trim() !== '' && isValidSyncFrequency(parsed);
  const needsApproval = isValid && requiresSyncFrequencyApproval(parsed);

  const visibleTimeCount = isValid ? Math.min(parsed, MAX_SCHEDULED_SYNC_TIMES) : 0;
  const visibleTimes = timeInputs.slice(0, visibleTimeCount);
  const enteredTimes = visibleTimes.filter((time) => time !== '');
  const hasInvalidTime = enteredTimes.some((time) => !isValidSyncTime(time));
  const hasDuplicateTimes = new Set(enteredTimes).size !== enteredTimes.length;
  // Once an integration has anchors the IS cannot cleanly unset them (null fails
  // its schema validation and [] trips nightly scheduler alerts), so the admin
  // must keep at least one.
  const removesAllStoredTimes = storedTimes.length > 0 && enteredTimes.length === 0;
  const timesError = resolveTimesError({
    hasInvalidTime,
    hasDuplicateTimes,
    removesAllStoredTimes,
  });

  // "HH:mm" strings sort chronologically; sorted so the saved schedule reads in
  // day order regardless of the slot the admin typed each time into.
  const submittedTimes = [...enteredTimes].sort();

  const isCountUnchanged = parsed === effectiveCurrent;
  const areTimesUnchanged = submittedTimes.join(',') === [...storedTimes].sort().join(',');
  const isUnchanged = isCountUnchanged && areTimesUnchanged;

  const canSubmit =
    isValid && !timesError && !isUnchanged && Boolean(serviceName) && !isUpdatingSyncFrequency;

  const currentLabel = resolveCurrentLabel(explicitCount, storedTimes.length, effectiveCurrent);

  const handleTimeChange = (index: number, value: string) => {
    setTimeInputs((previous) => previous.map((time, i) => (i === index ? value : time)));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    updateSyncFrequency(
      {
        mosaicTeamId,
        serviceName,
        targetServiceId,
        customDailyFetchCount: parsed,
        // Stored anchors always win over a lower count and survive when the key
        // is omitted, so whenever the admin has (or had) times we send the
        // sorted, trimmed array to replace the stored one wholesale. Never sent
        // empty — timesError blocks clearing an integration that has anchors.
        ...(submittedTimes.length > 0 ? { customDailyFetchTime: submittedTimes } : {}),
        tenant,
        realmId,
      },
      {
        onSuccess: () => {
          onUpdated?.();
          toggle();
        },
      },
    );
  };

  return (
    <Modal open={isOpen} onClose={isUpdatingSyncFrequency ? undefined : toggle} modalWidth={450}>
      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Change Sync Frequency
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Set how many times per day this integration fetches data. Current: {currentLabel}.
        </Typography>

        <form onSubmit={handleSubmit}>
          <TextField
            label="Daily syncs"
            type="number"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            fullWidth
            size="small"
            autoFocus
            disabled={isUpdatingSyncFrequency}
            inputProps={{
              min: MIN_SYNC_FREQUENCY,
              max: MAX_SYNC_FREQUENCY,
              step: 1,
            }}
            error={inputValue.trim() !== '' && !isValid}
            helperText={
              inputValue.trim() !== '' && !isValid
                ? `Enter a whole number between ${MIN_SYNC_FREQUENCY} and ${MAX_SYNC_FREQUENCY}.`
                : ' '
            }
          />

          {visibleTimeCount > 0 && (
            <Box mt={1}>
              <Typography variant="subtitle2">Scheduled sync times (UTC)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Optional. Each filled time is a guaranteed daily sync; empty slots are scheduled
                automatically.
              </Typography>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
                {visibleTimes.map((value, index) => {
                  const localTime = value ? formatUtcSyncTimeAsLocal(value) : null;
                  return (
                    <TextField
                      key={index}
                      label={`Sync time ${index + 1} (UTC)`}
                      type="time"
                      value={value}
                      onChange={(e) => handleTimeChange(index, e.target.value)}
                      size="small"
                      disabled={isUpdatingSyncFrequency}
                      InputLabelProps={{ shrink: true }}
                      error={value !== '' && !isValidSyncTime(value)}
                      helperText={localTime ? `≈ ${localTime} local` : ' '}
                    />
                  );
                })}
              </Box>
              {parsed > MAX_SCHEDULED_SYNC_TIMES && (
                <Typography variant="caption" color="text.secondary">
                  Up to {MAX_SCHEDULED_SYNC_TIMES} times can be pinned; the remaining{' '}
                  {parsed - MAX_SCHEDULED_SYNC_TIMES} daily{' '}
                  {parsed - MAX_SCHEDULED_SYNC_TIMES === 1 ? 'sync is' : 'syncs are'} scheduled
                  automatically in the largest gaps.
                </Typography>
              )}
              {timesError && (
                <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                  {timesError}
                </Typography>
              )}
            </Box>
          )}

          <Typography
            variant="body2"
            color={needsApproval ? 'warning.main' : 'text.secondary'}
            sx={{ mt: 1 }}
          >
            {needsApproval ? '⚠ ' : ''}
            {SYNC_FREQUENCY_APPROVAL_NOTE}
          </Typography>

          <Box display="flex" justifyContent="flex-end" gap={1} mt={3}>
            <Button variant="outlined" onClick={toggle} disabled={isUpdatingSyncFrequency}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={!canSubmit}>
              {isUpdatingSyncFrequency ? <CircularProgress size={20} color="inherit" /> : 'Save'}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default ChangeSyncFrequencyModal;
